/* eslint-disable import/no-cycle */
import React from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { omit } from 'lodash';
import { toast } from 'react-toastify';

import { useCart } from '@hooks/cart/useCart';
import { useCheckoutEditUserPersonalDataMutation } from '@hooks/checkout/useCheckoutEditUserPersonalDataMutation';
import { useCheckoutSignUpMutation } from '@hooks/checkout/useCheckoutSignUpMutation';
import { useUserEmailLookupMutation } from '@hooks/checkout/useUserEmailLookupMutation';
import { usePhrases } from '@hooks/context/usePhrases';
import { useNlAddressValidationMutation } from '@hooks/useAddressValidation';
import { useLocale } from '@hooks/useLocale';
import { useLogger } from '@hooks/useLogger';
import { useUser } from '@hooks/user/useUser';
import { useRedirect } from '@hooks/useRedirect';
import getRuntimeConfig from '@utils/getRuntimeConfig';
import { buildPath } from '@utils/paths/build-paths';
import { Paths } from '@utils/paths/paths';

import { CheckoutRegisterFormDe } from './de/CheckoutRegisterFormDe';
import { CheckoutRegisterFormNl } from './nl/CheckoutRegisterFormNl';

import { defaultContactSpots } from '@helpers/contact-spots';
import { getGaClientId } from '@services/gtm/gtm';
import { useCheckoutCreateFinanceOrderMutation } from '@hooks/checkout/useCheckoutCreateFinanceOrderMutation';
import Loader from '@components/Loader/Loader';
import { OfferItemType } from 'types/offer.types';

dayjs.extend(customParseFormat);

const CountryForm = {
  nl: CheckoutRegisterFormNl,
  de: CheckoutRegisterFormDe,
};

const salutationDefaultEntries = [
  { option: 'Mrs.', value: 'mrs' },
  { option: 'Mr.', value: 'mr' },
];

const TermsToPresetNameMap = {
  12: 'bluemovement1',
  24: 'bluemovement2',
  36: 'bluemovement3',
  48: 'bluemovement4',
  60: 'bluemovement5',
};
const sanitize = value => value?.trim()?.replace(/\s+/g, ' ');

export const CheckoutRegisterForm = props => {
  const { logger } = useLogger({ context: 'CheckoutRegisterForm' });
  const { country, locale, language } = useLocale();
  const { data: cart, isLoading } = useCart();
  const { data: user, isLoading: isUserLoading } = useUser();
  const errorPhrases = usePhrases({ name: 'errors', rawObject: true });
  const salutationPhrases = usePhrases({ name: 'salutations', rawObject: true });
  const salutationPhrasesEntries = Object.entries(salutationPhrases);
  const salutationEntries =
    salutationPhrasesEntries.length > 0
      ? salutationPhrasesEntries.map(([value, phrase]) => {
          return { option: phrase.value, value };
        })
      : salutationDefaultEntries;

  const signUpMutation = useCheckoutSignUpMutation();
  const updateUserMutation = useCheckoutEditUserPersonalDataMutation();
  const nlAddressValidationMutation = useNlAddressValidationMutation();
  const useUserLookupMutation = useUserEmailLookupMutation();
  const checkoutCreateFinanceOrderMutation = useCheckoutCreateFinanceOrderMutation();

  const redirect = useRedirect();

  const isFinancingPage = props.isFinancingPage || false;

  if (!cart || isLoading || isUserLoading) {
    return <Loader />;
  }

  // TODO Targo: refactor onSubmit later - BLUEMOVE-6447
  const onSubmit = async data => {
    logger.info({
      message: `User id ${user?.id} is starting submitting form with cart id ${cart?.id}`,
      params: { cartId: cart?.id, userId: user?.id },
    });

    let userIdToValidate;
    try {
      const {
        birthdate,
        customerType,
        deliveryInfo,
        isDeliveryInfo,
        isDifferentShippingAddress,
        differentShippingAddress,
        emailConsents,
        salutation,
        password,
        phoneNumber,
      } = data;
      if (country === 'nl' && ['aws-prod'].includes(getRuntimeConfig('ENVIRONMENT_NAME'))) {
        const address = await nlAddressValidationMutation.mutateAsync({
          postalCode: data.postalCode.toUpperCase().replace(/\s/g, ''),
          houseNumber: parseInt(data.houseNumber, 0),
        });

        const isAddressCorrect = address.city === data.city && address.street === data.street;

        if (!isAddressCorrect) {
          throw Error('Incorrect address');
        }
      }

      if (!cart) {
        throw Error('Cart not found');
      }

      const firstName = sanitize(data.firstName);
      const lastName = sanitize(data.lastName);
      const street = sanitize(data.street);
      const postalCode = sanitize(data.postalCode);
      const city = sanitize(data.city);
      const houseNumber = sanitize(data.houseNumber);
      const houseNumberAddon = sanitize(data.houseNumberAddon);
      const email = user?.email || data?.email;
      const shippingAddress = isDifferentShippingAddress && {
        firstName,
        lastName,
        phone: differentShippingAddress.phoneNumber,
        street: sanitize(differentShippingAddress.street),
        houseNumber: sanitize(differentShippingAddress.houseNumber),
        houseNumberAddon: sanitize(differentShippingAddress.houseNumberAddon),
        postalCode: sanitize(differentShippingAddress.postalCode),
        city: sanitize(differentShippingAddress.city),
        country,
      };

      const billingAddress = {
        firstName,
        lastName,
        phone: phoneNumber,
        city,
        street,
        houseNumber,
        houseNumberAddon,
        country,
        postalCode,
      };

      const createUserDto = {
        customerType,
        salutation,
        email,
        firstName,
        lastName,
        phone: phoneNumber,
        password,
        birthdate: !!birthdate?.length ? dayjs(birthdate, 'DD-MM-YYYY', true).format('YYYY-MM-DD') : undefined,
        postalCode,
        houseNumber,
        houseNumberAddon,
        street,
        city,
        extraDeliveryInformation: (isDeliveryInfo && deliveryInfo.note) || '',
        numberOfReturnedAssets: cart.numberOfReturnedAssets,
        acceptanceOfCommercialApproach: !!emailConsents,
        dryerConnector: cart.dryerConnector?.toString() || '',
        contactSpots: defaultContactSpots,
        cartId: cart.id,
        gaClientId: getGaClientId(),
        billingAddress,
        shippingAddress: shippingAddress || billingAddress,
        language,
      };

      if (!user) {
        const result = await useUserLookupMutation.mutateAsync({ email });

        if (result) {
          logger.warn({
            message: `User ${userIdToValidate || user?.id} have used existing email ${email}`,
            params: { userId: userIdToValidate, cartId: cart?.id, result },
          });

          return;
        }

        const singedUpResponse = await signUpMutation.mutateAsync({ ...createUserDto, isFinancingPage });
        userIdToValidate = singedUpResponse.userId;
      } else {
        logger.info({
          message: `User ${userIdToValidate || user?.id} is trying to update existing data`,
          params: { userId: userIdToValidate, cartId: cart?.id },
        });

        userIdToValidate = await updateUserMutation.mutateAsync({
          updateUserDto: {
            ...omit(createUserDto, ['password']),
            id: user.id,
            bmId: user.bmId,
            cbId: user.cbId,
            fdId: user.fdId,
            email: user.email,
          },
          isFinance: isFinancingPage,
        });
      }

      logger.info({
        message: `User ${userIdToValidate || user?.id} is checking risk validation`,
        params: { userId: userIdToValidate || user?.id, cartId: cart?.id },
      });

      if (!props.isFinancingPage) {
        const queryParams =
          data.testCreditCheckValue && data.testIdCheckValue
            ? { testCreditCheckValue: data.testCreditCheckValue, testIdCheckValue: data.testIdCheckValue }
            : {};

        return redirect({
          url: buildPath({
            pageId: Paths.CheckoutCreditCheck,
            queryParams,
            locale,
          }),
        });
      }

      logger.info({
        message: `Start processing finance checkout`,
        params: {
          userId: userIdToValidate || user?.id,
          cartId: cart?.id,
        },
      });

      logger.info({
        message: `Checkout finance order creation is in progress`,
      });

      const checkoutCreateFinanceOrder = await checkoutCreateFinanceOrderMutation.mutateAsync({
        userId: userIdToValidate || user?.id,
        cartId: cart?.id,
      });

      logger.info({
        message: `Checkout finance order creation is done with success`,
        params: {
          checkoutCreateFinanceOrder,
        },
      });

      if (data?.targo?.skipCheckout) {
        toast('Processing finance checkout (skipping the Targo part)', {
          type: toast.TYPE.WARNING,
          position: toast.POSITION.TOP_CENTER,
        });

        return setTimeout(() => {
          redirect({
            url: buildPath({
              pageId: Paths.CheckoutFinanceOrderConfirmation,
              locale,
            }),
          });
        }, 3000);
      }

      logger.info({
        message: `Creating Targo checkout form`,
      });

      const fundingPeriod =
        cart?.items.filter(
          cartItem => (cartItem.offerItem && cartItem.offerItem?.type === OfferItemType.Main) || cartItem.plan
        )?.[0]?.pricing?.months || order?.financeContract?.loanPeriod;
      const targoUrl = 'https://www.targobank.de/de/app/indirectloanrequest.html';
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = targoUrl;
      const totalMonthlyPrice = cart?.totalMonthlyPrice;
      const APP_URL = getRuntimeConfig('APP_URL');

      const isProd = getRuntimeConfig('ENVIRONMENT_NAME') === 'aws-prod';

      Object.entries({
        koop_id: TermsToPresetNameMap[fundingPeriod] || 'bluemovement1',
        dealerID: '806554',
        documentno: checkoutCreateFinanceOrder.financeContractId,
        amount: (totalMonthlyPrice * fundingPeriod) / 100,
        surname: isProd ? data.lastName : 'xyz',
        firstname: data.firstName,
        gender: data.salutation === 'mr' ? 'm' : 'w',
        street: data.street,
        streetnumber: data.houseNumber,
        zip: data.postalCode,
        city: data.city,
        telnocode: data.phoneNumber.substr(0, 4),
        telno: data.phoneNumber.substr(5),
        email: data.email,
        dealerShopURL: `${APP_URL}/checkout-redirect?url=${APP_URL}${buildPath({
          pageId: Paths.CheckoutFinanceOrderConfirmation,
          locale,
        })}`,
        dealerRejectURL: `${APP_URL}/checkout-redirect?url=${APP_URL}${buildPath({
          pageId: Paths.CheckoutFinanceOrderNotAccepted,
          locale,
        })}`,
        dealerAbortURL: `${APP_URL}/checkout-redirect?url=${APP_URL}${buildPath({
          pageId: Paths.CheckoutFinanceCustomerDetails,
          locale,
        })}`,
      }).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);

      logger.info({
        message: `Submitting Targo checkout form`,
      });

      form.submit();
    } catch (error) {
      logger.error({
        message: `Error during registering by user ${userIdToValidate || user?.id}`,
        params: {
          userId: userIdToValidate || user?.id,
          cartId: cart?.id,
          errorMessage: error.message || error.data?.errors?.[0]?.message,
        },
      });

      toast(errorPhrases?.['internal-server-error']?.value || 'Internal Server Error', {
        type: toast.TYPE.ERROR,
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  const CountryCheckoutRegisterForm = CountryForm[country];

  return (
    <CountryCheckoutRegisterForm
      user={user}
      cart={cart}
      salutationEntries={salutationEntries}
      onSubmit={onSubmit}
      isRedirecting={props.isRedirecting}
      validationMessages={props.validationMessages}
      {...props}
    />
  );
};
